import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, FileClock, CheckCircle2, XCircle, CalendarClock, ShieldAlert, MonitorSmartphone } from "lucide-react";
import api from "@/lib/api";

const ICON = {
  tariff_submitted: FileClock,
  tariff_approved: CheckCircle2,
  tariff_rejected: XCircle,
  reminder: CalendarClock,
  security: ShieldAlert,
  device_request: MonitorSmartphone,
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationDrawer({ notifications, reloadNotifications }) {
  const unread = notifications.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    reloadNotifications();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    reloadNotifications();
  };

  return (
    <Sheet onOpenChange={(o) => { if (o) reloadNotifications(); }}>
      <SheetTrigger asChild>
        <button className="relative h-9 w-9 rounded-full grid place-items-center hover:bg-secondary transition-colors" data-testid="notifications-bell-button">
          <Bell className="h-5 w-5 text-foreground" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono grid place-items-center">
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-serif text-2xl">Notifications</SheetTitle>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="text-xs" data-testid="mark-all-read-button">
              <CheckCheck className="h-4 w-4 mr-1" /> Mark all read
            </Button>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4 space-y-2">
          {notifications.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-16">You're all caught up.</div>
          )}
          {notifications.map((n) => {
            const Icon = ICON[n.type] || Bell;
            return (
              <button
                key={n.id}
                onClick={() => markOne(n.id)}
                data-testid={`notification-${n.id}`}
                className={`w-full text-left flex gap-3 p-3 rounded-lg border transition-colors ${
                  n.read ? "bg-card border-border" : "bg-accent border-primary/30"
                }`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${n.read ? "text-muted-foreground" : "text-primary"}`} />
                <div className="min-w-0">
                  <p className="text-sm text-foreground leading-snug">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
